import { useAuth } from '@clerk/clerk-react'
import { useOrganizationList } from '@clerk/clerk-react'
import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { SignUpWizard } from './components/sign-up-wizard'
import { CrmV3SignUpRedirect } from '@/components/auth/crm-v3-sign-up-redirect'
import { PartnerOtpSignIn } from '@/components/auth/partner-otp-sign-in'
import { resolveSatellite } from '@/config/clerk-satellite'
import { crmV3SignUpUrlFor } from '@/hooks/use-crm-v3-redirect'

/**
 * Resolves the org slug to redirect to when the user already has an account.
 * Returns null if no org is available.
 */
const getFirstOrgSlug = (
	memberships: { data?: { organization: { slug: string | null } }[] } | undefined,
): string | null =>
	memberships?.data?.[0]?.organization?.slug ?? null

export default function SignUpNewPage() {
	const { isSignedIn, isLoaded: authLoaded } = useAuth()
	const { userMemberships, isLoaded: membershipsLoaded } = useOrganizationList({
		userMemberships: { pageSize: 1 },
	})

	if (!authLoaded || (isSignedIn && !membershipsLoaded)) {
		return (
			<div className="flex h-screen items-center justify-center">
				<Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
			</div>
		)
	}

	// Partner whitelabel satellite + not signed in: the multi-step wizard
	// would call signUp.create (blocked on satellites) and show a Google
	// button (leaks the Tagada OAuth app name). Route to the self-owned
	// OTP flow instead — it auto-creates the user (implicit sign-up) and,
	// once signed in with no org, OnboardingRedirect sends them back here
	// in ?mode=sso to run the satellite-safe org-creation wizard.
	if (!isSignedIn && resolveSatellite()) {
		return <PartnerOtpSignIn mode="sign-up" />
	}

	const orgSlug = getFirstOrgSlug(userMemberships)

	if (isSignedIn && orgSlug) {
		return <Navigate to={`/org/${orgSlug}`} replace />
	}

	const v3SignUpUrl = crmV3SignUpUrlFor(window.location.hostname)
	if (v3SignUpUrl) {
		return <CrmV3SignUpRedirect url={v3SignUpUrl} />
	}

	return <SignUpWizard />
}
